import React from 'react';
import './App.css';
import Logo from './components/Logo';
import SearchBar from './components/SearchBar';
import SearchResult from './components/SearchResult';
import SkeletonCard from './components/SkeletonCard';
import TrailerOverlay from './components/TrailerOverlay';
import RatingFilter from './components/filters/RatingFilter';
import DecadeFilter from './components/filters/DecadeFilter';
import GenreFilter from './components/filters/GenreFilter';
import { useMovieAndTV } from './hooks/useMovieAndTV';
import { FaTv, FaDice, FaRedo, FaMagic, FaCompass } from 'react-icons/fa';

function App() {
  const {
    searchQuery,
    setSearchQuery,
    suggestions,
    fetchSuggestions,
    results,
    loading,
    randomLoading,
    selectedItem,
    showTrailerOverlay,
    trailerURL,
    errorMessage,
    handleSearch,
    handlePopularMovies,
    handlePopularTVShows,
    openTrailerOverlay,
    closeTrailerOverlay,
    ratingFilter,
    setRatingFilter,
    decadeFilter,
    setDecadeFilter,
    genreFilter,
    setGenreFilter,
    isRandomMode,
    activeCategory,
    resetSearch
  } = useMovieAndTV();

  const isDiscovering = loading || randomLoading;
  const hasResults = results && results.length > 0;
  const showResultsSection = isDiscovering || hasResults || errorMessage || isRandomMode;

  const handleReroll = () => {
    if (activeCategory === 'tv') {
      handlePopularTVShows();
    } else {
      handlePopularMovies();
    }
  };

  const getSectionTitle = () => {
    if (isRandomMode) {
      if (activeCategory === 'tv') return 'Curated TV Shows';
      return 'Curated Movies';
    }
    if (searchQuery) {
      return `Results for "${searchQuery}"`;
    }
    return 'Featured Discoveries';
  };

  return (
    <div className="App">
      <div className="app-container">
        {/* Navigation Bar */}
        <header className="app-navbar">
          <div className="brand-section" onClick={resetSearch} role="button" tabIndex={0}>
            <Logo size="medium" />
            <span className="brand-pill">
              <span className="live-dot" /> Live
            </span>
          </div>

          <div className="nav-center-search">
            <SearchBar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              suggestions={suggestions}
              fetchSuggestions={fetchSuggestions}
              errorMessage={errorMessage}
              handleSearch={handleSearch}
              resetSearch={resetSearch}
            />
          </div>

          <div className="nav-stats">
            <div className="stat-chip" title="Real-time NL streaming availability">
              <span className="highlight-green">●</span> NL Streaming
            </div>
            <div className="stat-chip" title="Instant TMDB metadata">
              <span className="highlight-purple">⚡</span> Ultra-Fast
            </div>
          </div>
        </header>

        {/* Hero Discovery Hub */}
        <main>
          <section className="hero-hub">
            <div className="hero-content">
              <div className="hero-tag">
                <FaMagic className="green-accent" />
                <span>Next-Gen Cinema Discovery</span>
              </div>
              <h2 className="hero-title">
                What to watch tonight? <br />
                <span className="duo-gradient-text">Instant curation, zero hassle.</span>
              </h2>
              <p className="hero-subtitle">
                Roll the dice for top-rated films & binge-worthy series, filtered by your favorite decade, genre, and rating.
              </p>
            </div>

            {/* High-impact Action Buttons */}
            <div className="hero-action-buttons">
              <button
                className="spin-button-duo primary-spin"
                onClick={handlePopularMovies}
                disabled={isDiscovering}
                aria-label="Discover random movies"
              >
                <span className="btn-icon">
                  <FaDice />
                </span>
                <span>Random Movies</span>
              </button>

              <button
                className="spin-button-duo secondary-spin"
                onClick={handlePopularTVShows}
                disabled={isDiscovering}
                aria-label="Discover random TV shows"
              >
                <span className="btn-icon">
                  <FaTv />
                </span>
                <span>Random TV Shows</span>
              </button>
            </div>

            {/* Filter Bar Hub */}
            <div className="filter-hub-bar">
              <div className="filter-group-wrapper">
                <GenreFilter
                  genreFilter={genreFilter}
                  setGenreFilter={setGenreFilter}
                />
                <DecadeFilter
                  decadeFilter={decadeFilter}
                  setDecadeFilter={setDecadeFilter}
                />
                <RatingFilter
                  ratingFilter={ratingFilter}
                  setRatingFilter={setRatingFilter}
                />
              </div>
            </div>
          </section>

          {/* Results & Discovery Grid Section */}
          {showResultsSection && (
            <section className="results-section" aria-live="polite">
              <div className="section-header-bar">
                <div className="section-title-wrap">
                  <div className="section-title-icon">
                    <FaCompass />
                  </div>
                  <h3 className="section-title">
                    {getSectionTitle()}
                    {hasResults && !isDiscovering && (
                      <span className="count-badge">{results.length} Titles</span>
                    )}
                  </h3>
                </div>

                <div className="section-actions">
                  {isRandomMode && (
                    <button
                      className="action-btn-reroll"
                      onClick={handleReroll}
                      disabled={isDiscovering}
                      title="Shuffle again with active filters"
                    >
                      <FaRedo /> Re-roll Selection
                    </button>
                  )}
                  <button
                    className="action-btn-clear"
                    onClick={resetSearch}
                    title="Clear current results"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {errorMessage && !isDiscovering && !hasResults && (
                <div className="empty-state-card">
                  <div className="empty-state-icon">🍿</div>
                  <h4 className="empty-state-title">No Titles Found</h4>
                  <p className="empty-state-desc">{errorMessage}</p>
                  <button
                    className="spin-button-duo primary-spin"
                    style={{ margin: '0 auto' }}
                    onClick={handlePopularMovies}
                  >
                    Discover Popular Movies
                  </button>
                </div>
              )}

              <div className="results-grid">
                {isDiscovering ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <SkeletonCard key={`skeleton-${index}`} />
                  ))
                ) : (
                  results.map((item) => (
                    <SearchResult
                      key={item.id}
                      item={item}
                      openTrailerOverlay={openTrailerOverlay}
                    />
                  ))
                )}
              </div>
            </section>
          )}
        </main>

        {/* Cinema Detail & Trailer Modal */}
        {showTrailerOverlay && (
          <TrailerOverlay
            selectedItem={selectedItem}
            trailerURL={trailerURL}
            closeTrailerOverlay={closeTrailerOverlay}
          />
        )}
      </div>
    </div>
  );
}

export default App;